import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Stack,
  Alert,
  Box,
  useTheme,
  alpha,
  Snackbar,
  useMediaQuery
} from '@mui/material';
import { useFormValidation, useOTPManagement, useIFSCLookup, useBankForm } from './hooks';
import { ModalHeader, AccountTypeSelector, BankInformationForm, OTPVerification, ModalActions } from './components';
import { INITIAL_FORM_DATA } from './constants/bankModalConstants';


const AddBankModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { errors, validateForm, clearError, setError } = useFormValidation();
  const {
    otpSent,
    isLoading: otpLoading,
    timer,
    canResendOTP,
    snackbarMessage,
    snackbarSeverity,
    showSnackbar,
    setShowSnackbar,
    handleSendOTP,
    resetOTPState
  } = useOTPManagement();

  const { isIfscLoading, debouncedLookupIFSC } = useIFSCLookup();

  const {
    formData,
    loading,
    handleChange,
    updateFormData,
    resetForm,
    handleSubmit
  } = useBankForm();

  useEffect(() => {
    if (open) {
      handleSendOTP();
    } else {
      resetOTPState();
      resetForm();
    }
  }, [open, handleSendOTP, resetOTPState, resetForm]);

  const handleFormChange = (e) => {
    const ifscLookupHandler = (ifscCode) => {
      debouncedLookupIFSC(ifscCode, handleIFSCSuccess, handleIFSCError);
    };
    handleChange(e, ifscLookupHandler, clearError);
  };

  const handleIFSCSuccess = (data) => {
    updateFormData({
      bank_name: data?.BANK || '',
      city: data?.CITY || '',
      state: data?.STATE || '',
    });
    clearError('ifsc');
  };

  const handleIFSCError = (message) => {
    setError('ifsc', message);
  };

  const handleFormSubmit = async () => {
    const success = await handleSubmit(validateForm, setError);
    if (success) {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxWidth: 500,
            width: '100%',
            height: isMobile ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <ModalHeader onClose={onClose} />

        <DialogContent sx={{
          p: isMobile ? 2 : 3,
          flex: isMobile ? 1 : 'initial',
          overflow: 'auto'
        }}>
          <Stack spacing={isMobile ? 2.5 : 3}>
            {errors.submit && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                }}
              >
                {errors.submit}
              </Alert>
            )}

            <AccountTypeSelector
              value={formData.account_type}
              onChange={handleFormChange}
            />

            <BankInformationForm
              formData={formData}
              errors={errors}
              isIfscLoading={isIfscLoading}
              onChange={handleFormChange}
            />

            <OTPVerification
              formData={formData}
              errors={errors}
              timer={timer}
              otpSent={otpSent}
              isLoading={otpLoading}
              canResendOTP={canResendOTP}
              onChange={handleFormChange}
              onSendOTP={handleSendOTP}
            />
          </Stack>
        </DialogContent>

        <ModalActions
          onClose={onClose}
          onSubmit={handleFormSubmit}
          loading={loading}
        />
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: isMobile ? 'center' : 'right'
        }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddBankModal;